import { useState } from 'react'
import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react'
import type { ArrivalDto, WorksiteInfoDto } from '../../../types/canvasser'
import { WorksiteInfoSheet } from '../walk/WorksiteInfoSheet'
import { readScriptLanguage, writeScriptLanguage, type ScriptLanguage } from '../../../lib/walkPrefs'

/**
 * [Spec §15.1 D1, Phase 4 P4-2] Between "I'm here" and talking to anyone at a worksite. Shows the
 * server-resolved arrival (never Notes / POC phone / email). With no arrival it is a compact
 * "At {worksite}" chooser — the three actions live here, so the step is never skipped.
 */
export function BeforeYouGoInStep({ workplaceLabel, address, arrival, worksiteInfo, onInside, onNobodyAvailable, onCouldntGetIn, isSaving, canCouldntGetIn = true }: {
  workplaceLabel: string | null; arrival: ArrivalDto | null
  /** [O3] For the "Worksite info" sheet. */
  address: string; worksiteInfo: WorksiteInfoDto | null
  onInside: () => void; onNobodyAvailable: () => void; onCouldntGetIn: () => void; isSaving: boolean
  /** [P6-8] False when the organizer switched Inaccessible off. */
  canCouldntGetIn?: boolean
}) {
  const [infoOpen, setInfoOpen] = useState(false)
  const [lang, setLang] = useState<ScriptLanguage>(readScriptLanguage)
  const text = lang === 'es' && arrival?.textEs ? arrival.textEs : arrival?.text ?? arrival?.textEs ?? null
  const toggle = () => {
    const next = lang === 'en' ? 'es' : 'en'
    setLang(next)
    writeScriptLanguage(next)
  }
  const hasArrival = !!(arrival?.text || arrival?.textEs || arrival?.askFor)
  return (
    <VStack flex={1} align="stretch" justify="flex-end" gap={3} px={4} pb={6}>
      <HStack justify="space-between">
        <Text fontSize="xl" fontWeight="bold">{hasArrival ? 'Before you go in' : `At ${workplaceLabel ?? 'this worksite'}`}</Text>
        {arrival?.text && arrival?.textEs && (
          <Button size="sm" minW="44px" minH="44px" variant="outline" borderColor="border" onClick={toggle}>
            {lang === 'en' ? 'ES' : 'EN'}
          </Button>
        )}
      </HStack>
      {text && <Box p={3} borderRadius="md" bg="bg.subtle"><Text fontSize="lg">{text}</Text></Box>}
      {arrival?.askFor && <Text fontWeight="semibold">{`Ask for: ${arrival.askFor}`}</Text>}
      <Button minH="44px" variant="outline" borderColor="border" onClick={() => setInfoOpen(true)}>Worksite info</Button>
      <WorksiteInfoSheet open={infoOpen} onClose={() => setInfoOpen(false)} workplaceLabel={workplaceLabel}
        address={address} info={worksiteInfo} />
      <Button minH="56px" bg="accent" color="accent.fg" onClick={onInside}>I'm inside</Button>
      <Button minH="52px" variant="outline" borderColor="border" disabled={isSaving} onClick={onNobodyAvailable}>Nobody available</Button>
      {canCouldntGetIn && (
        <Button minH="52px" variant="outline" borderColor="border" disabled={isSaving} onClick={onCouldntGetIn}>Couldn't get in</Button>
      )}
    </VStack>
  )
}
